/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
/**
 * ProjectModels.ts
 * ----------------
 * Contains all core data models (classes and interfaces) for Project State.
 *
 * - Defines ProjectStorableState: the serializable, persistent subset of the project used for saving/loading.
 * - Defines ProjectState: extends ProjectStorableState with runtime-only fields and additional stateful properties.
 * - Contains interfaces for auxiliary data (e.g., DataSourcePair, AsyncProjectData).
 *
 * This file should be imported wherever you need to reference project state types or models, and forms the foundation for
 * serialization/deserialization and in-memory state.
 */
import { ApolloClient } from "@apollo/client";
import { AxiosInstance } from "axios";
import {
  ReactFlowEdge,
  ReactFlowNode,
} from "../TransformEngine/TransformNodes/Util/TransformEngineDataUtils";
import { UUID, createUUID } from "../Utils/Types";
import { SiteProps } from "../Utils/SiteProps";
import { PromiseState, IDLE } from "../Informedb/PromiseState";
import { FileInfoPair } from "../Api/DataTransformation";
import { ComposerNodeDescriptor, InputNodeDescriptor } from "Api";

export interface AsyncProjectData {
  projects: ProjectStorableState[];
}

export class ProjectStorableState {
  projectID: UUID = createUUID();
  projectName: string = "Untitled mapping";

  // the mapping objects
  rfNodes: ReactFlowNode[] = [];
  rfEdges: ReactFlowEdge[] = [];

  // Input Node Descriptors
  inputNodeDescriptors: InputNodeDescriptor[] = [];
  composerNodes?: ComposerNodeDescriptor[] = [];
}

/**
 * ProjectState extends ProjectStorableState and contains
 * all fields needed for runtime management and async tracking.
 */
export class ProjectState extends ProjectStorableState {
  // For calling GraphQL without useQuery
  apolloClient: ApolloClient<any> = undefined;

  // For interacting with the backend
  axiosContext: AxiosInstance = undefined;
  siteProps: SiteProps = undefined;

  // Stuff loaded asynchronously
  dataLoadAsync: PromiseState<AsyncProjectData> = IDLE;

  // Server operations performed asynchronously
  performMappingAsync: PromiseState<FileInfoPair | string | boolean> = IDLE;
  saveMappingAsync: PromiseState<ProjectStorableState[]> = IDLE;

  // Saved projects may change from run to run
  bufferedProjects: ProjectStorableState[] = [];

  // Track if edge is clicked
  edgeClicked: boolean = false;

  projectTitleEditInProgress?: boolean = false;
  projectTitleInProgress?: string;

  // There are some components that require a changing
  // piece of state to signal that a rerender needs to occur after
  // non-user-initiated actions, e.g. values being updated from an AJAX response
  rerenderKey: number = 1;
}
